/**
 * The Hub user-role model — the EMPLOYEE vs CLIENT distinction.
 *
 * Hub has exactly four roles, stored as `public.roles` rows and referenced by
 * `app_users.role_id`. There is NO `is_employee` / `is_client` boolean column —
 * the distinction is derived ENTIRELY from which role a user holds.
 *
 * | Role           | Class    | What they may do                                                                       |
 * |----------------|----------|----------------------------------------------------------------------------------------|
 * | `wtc_admin`    | employee | Everything. RLS `is_admin()` → sees ALL contacts/tickets/deals.                          |
 * | `wtc_crm`      | employee | SALES: contacts, companies, deals, proposals, meetings. Does NOT work tickets or time.   |
 * | `wtc_employee` | employee | SERVICE DELIVERY: tickets, time entries, and the contacts behind them.                   |
 * | `client_user`  | client   | Limited self-service portal — sees ONLY their own data, gated by `contact_user_access`.  |
 *
 * ## Why this is a package
 *
 * Four apps read the SAME `app_users.role_id` in the SAME Supabase project, and
 * three of them had grown a private copy of this vocabulary and these
 * predicates. The copies had already drifted — `apps/unifiedtime`'s
 * `isEmployeeRole` returned true for `client_user` as well, so all four of its
 * predicates were "is a recognized role" wearing four different names.
 *
 * A role predicate is a security decision. Two implementations means a fix
 * lands in one and the other keeps answering the old way, silently, in an app
 * reading the same rows.
 *
 * ## What is deliberately NOT here
 *
 * - **Route guards.** Hub's `require*Auth*` family depends on its service-role
 *   client, its logger and `NextResponse`. Packaging the decision would put a
 *   security-critical path behind a `dist/` rebuild and a submodule pointer
 *   bump. The model is pure and safe to share; the guards are not.
 * - **Presentation.** Each app renders roles differently on purpose — Hub's
 *   admin table says "Admin" where its Slack copy says "Admin (full access)".
 *   The labels below are the LONG form; an app wanting a short one keeps it.
 * - **A capability matrix.** Hub carried a 19-capability × 4-role
 *   `ROLE_PERMISSIONS` table that nothing imported, and `unifiedtime` inherited
 *   it by copy. What Hub actually enforces is the role name, the per-contact
 *   grant tables, and `app_users.sensitive_feature_access`. Do not rebuild it
 *   here.
 *
 * ## `wtc_crm` and `wtc_employee` are SIBLINGS, not a superset and a subset
 *
 * This is the distinction most often got backwards, including by Hub's own
 * admin UI, which described CRM as "employee access plus CRM functions" until
 * 2026-09. It is not. `wtc_crm` sells — contacts, companies, deals, proposals,
 * meetings — and does not work tickets or time entries. `wtc_employee` does
 * the service delivery: tickets, time entries, and the contacts behind them.
 * Neither contains the other, and `isEmployeeRole` returning true for both
 * means "is WTC staff", never "has the employee role's job".
 *
 * How access is really enforced (Supabase RLS — see the migrations):
 *   - `is_admin()` / `is_employee()` / `is_client_user()` key off the role name.
 *   - An ADMIN sees all contacts; no per-row grant needed.
 *   - A CRM/EMPLOYEE user sees a contact only with a row in
 *     `employee_contact_access (user_id, contact_id)`.
 *   - A CLIENT_USER sees a contact only with an active row in
 *     `contact_user_access (user_id, contact_id, is_active, expires_at)`, or an
 *     active company-wide `company_user_access` row.
 *
 * So "grant employee access" = set an employee role (+ `employee_contact_access`
 * rows), and "grant client access" = set `client_user` + grant
 * `contact_user_access` / `company_user_access`. A role flip alone shows a
 * client nothing.
 */

export const HUB_ROLE = {
  ADMIN: "wtc_admin",
  CRM: "wtc_crm",
  EMPLOYEE: "wtc_employee",
  CLIENT: "client_user",
} as const;

export type HubRoleName = (typeof HUB_ROLE)[keyof typeof HUB_ROLE];

/** Roles that are WTC STAFF (internal employees) — `is_employee()` in RLS. */
export const EMPLOYEE_ROLE_NAMES: readonly HubRoleName[] = [
  HUB_ROLE.ADMIN,
  HUB_ROLE.CRM,
  HUB_ROLE.EMPLOYEE,
];

/** Roles that are external CLIENTS using the limited self-service portal. */
export const CLIENT_ROLE_NAMES: readonly HubRoleName[] = [HUB_ROLE.CLIENT];

/** The employee roles a teammate may be assigned when granting employee access. */
export const ASSIGNABLE_EMPLOYEE_ROLES: readonly HubRoleName[] = [
  HUB_ROLE.ADMIN,
  HUB_ROLE.CRM,
  HUB_ROLE.EMPLOYEE,
];

/** Every role Hub recognizes, in a stable order. */
export const ALL_HUB_ROLES: readonly HubRoleName[] = [
  HUB_ROLE.ADMIN,
  HUB_ROLE.CRM,
  HUB_ROLE.EMPLOYEE,
  HUB_ROLE.CLIENT,
];

/** Whether `value` is one of the four role names Hub recognizes. */
export function isHubRoleName(value: unknown): value is HubRoleName {
  return typeof value === "string" && (ALL_HUB_ROLES as readonly string[]).includes(value);
}

/** True for any WTC-staff role (admin / crm / employee). Excludes clients. */
export function isEmployeeRole(role: string | null | undefined): boolean {
  return !!role && (EMPLOYEE_ROLE_NAMES as readonly string[]).includes(role);
}

/** True for the external client-portal role. */
export function isClientRole(role: string | null | undefined): boolean {
  return !!role && (CLIENT_ROLE_NAMES as readonly string[]).includes(role);
}

/** True only for the full-access admin role (`is_admin()` in RLS). */
export function isAdminRole(role: string | null | undefined): boolean {
  return role === HUB_ROLE.ADMIN;
}

/** True only for the CRM role (`is_crm_user()` in RLS). An admin is NOT CRM. */
export function isCrmRole(role: string | null | undefined): boolean {
  return role === HUB_ROLE.CRM;
}

/**
 * Resolve a user's role from either the joined `roles` relation
 * (`app_users.role_id -> roles.name`, the canonical source) or a legacy scalar
 * `role` field.
 *
 * `app_users` has no `role` column, so the role lives only in the joined
 * `roles.name` — reading `user.role` alone always yields undefined. Callers go
 * through here rather than repeating the `||` chain.
 */
export function resolveUserRole(
  user: { role?: string | null; roles?: { name?: string | null } | null } | null | undefined
): string | null {
  return user?.roles?.name || user?.role || null;
}

/** Long-form label for a role name. Apps with a short form keep their own. */
export const HUB_ROLE_LABELS: Record<HubRoleName, string> = {
  [HUB_ROLE.ADMIN]: "Admin (full access)",
  [HUB_ROLE.CRM]: "CRM user",
  [HUB_ROLE.EMPLOYEE]: "Employee",
  [HUB_ROLE.CLIENT]: "Client (portal)",
};

export function roleLabel(role: string | null | undefined): string {
  if (isHubRoleName(role)) return HUB_ROLE_LABELS[role];
  return role ?? "—";
}

/** One-line description of what each role can do (UI / Slack copy). */
export const HUB_ROLE_DESCRIPTIONS: Record<HubRoleName, string> = {
  [HUB_ROLE.ADMIN]:
    "Full system access — sees every contact, ticket, deal, and the CEO dashboard.",
  [HUB_ROLE.CRM]:
    "Sales: contacts, companies, deals, proposals, meetings. Does not work tickets or time entries. Per-contact access controlled by employee_contact_access.",
  [HUB_ROLE.EMPLOYEE]:
    "Service delivery: tickets, time entries, and the contacts behind them. Per-contact access controlled by employee_contact_access.",
  [HUB_ROLE.CLIENT]:
    "Limited client portal — sees ONLY their own tickets/communications, scoped by contact_user_access.",
};

/**
 * Coarse user class for a role name — the single concept most callers want
 * ("is this an internal staff member or an external client?").
 */
export type HubUserClass = "employee" | "client" | "unknown";

export function userClassForRole(role: string | null | undefined): HubUserClass {
  if (isEmployeeRole(role)) return "employee";
  if (isClientRole(role)) return "client";
  return "unknown";
}

// ============================================================================
// ROLE SETS — named allow-lists for UI visibility
// ============================================================================
//
// Field, action and tab visibility in Hub is declared as an allow-list of role
// names. Those lists used to be written out by hand at ~160 call sites, which
// produced two problems a named set fixes.
//
// First, the same intent was spelled two ways with DIFFERENT results:
// `ROLE_COMBINATIONS.EMPLOYEE_AND_ADMIN` included `wtc_crm` (32 uses) while the
// literal `["wtc_employee", "wtc_admin"]` did not (118 uses). Identical meaning
// to a reader, opposite behaviour for a sales user.
//
// Second, a literal list does not say WHY a role is missing. `wtc_crm` absent
// from a ticket field is correct — CRM does not work tickets. `wtc_crm` absent
// from a DEAL field is a bug. Same three strings, different verdicts, and
// nothing in the array to tell them apart.
//
// These are UI AFFORDANCES, not a security boundary: a field hidden by an
// allow-list is still in the API response. See HUB_AUTHZ_LAYERS.

/** Every WTC staff role. Sales AND service delivery, plus admin. */
export const ALL_STAFF_ROLES: readonly HubRoleName[] = [
  HUB_ROLE.ADMIN,
  HUB_ROLE.CRM,
  HUB_ROLE.EMPLOYEE,
];

/** Staff plus the client portal — anyone with a recognized role. */
export const ALL_STAFF_AND_CLIENT_ROLES: readonly HubRoleName[] = [
  HUB_ROLE.ADMIN,
  HUB_ROLE.CRM,
  HUB_ROLE.EMPLOYEE,
  HUB_ROLE.CLIENT,
];

/**
 * Service delivery only — tickets, time entries, the work `wtc_employee` does.
 *
 * `wtc_crm` is absent ON PURPOSE, and that is the whole reason this set has a
 * name: CRM sells and does not work tickets or time entries, so a ticket field
 * that omits CRM is correct rather than an oversight. Do not "fix" this set by
 * adding CRM.
 */
export const SERVICE_STAFF_ROLES: readonly HubRoleName[] = [
  HUB_ROLE.ADMIN,
  HUB_ROLE.EMPLOYEE,
];

/** Service delivery plus the client portal (a client's own tickets/time). */
export const SERVICE_STAFF_AND_CLIENT_ROLES: readonly HubRoleName[] = [
  HUB_ROLE.ADMIN,
  HUB_ROLE.EMPLOYEE,
  HUB_ROLE.CLIENT,
];

/** Admin only. */
export const ADMIN_ONLY_ROLES: readonly HubRoleName[] = [HUB_ROLE.ADMIN];
